import { type NumberFieldStateOptions, useNumberFieldState } from "@react-stately/numberfield";
import cx from "classnames";
import { type ForwardRefRenderFunction, type RefObject, forwardRef, useRef } from "react";
import { useLocale, useNumberField } from "react-aria";

import styles from "../InputText/_.module.css";

import { InputNumberButton } from "./InputNumberButton";

export type InputNumberProps = Omit<NumberFieldStateOptions, "locale" | "isDisabled"> & {
	disabled?: boolean;
	size?: "lg" | "md" | "sm";
	fullWidth?: boolean;
	noChrome?: boolean;
	incrementButtonPosition?: "outside" | "right";
};

export const BaseInputNumber: ForwardRefRenderFunction<HTMLInputElement, InputNumberProps> = (
	{
		size = "sm",
		disabled = false,
		fullWidth = false,
		noChrome = false,
		incrementButtonPosition = "right",
		...props
	},
	ref
) => {
	props = Object.assign(props, { isDisabled: disabled });

	const { locale } = useLocale();
	const state = useNumberFieldState({ ...props, locale });
	const inputRef = useRef<HTMLInputElement>(null);
	const { labelProps, groupProps, inputProps, incrementButtonProps, decrementButtonProps } =
		useNumberField(props, state, (ref ?? inputRef) as RefObject<HTMLInputElement>);

	return (
		<div className={cx("flex items-center", fullWidth && "w-full")}>
			{props.label && (
				<label
					{...labelProps}
					className={cx("cursor-pointer text-primary mr-2", {
						"text-sm": size === "sm" || size === "md",
					})}
				>
					{props.label}
				</label>
			)}
			<div
				className={cx([
					styles["input-text"],
					"items-stretch",
					"group",
					"cursor-pointer",
					"bg-primary",
					"[&>*>svg]:fill-icon-default",
					"[&>*>svg]:hover:fill-icon-hover",
					"[&>*>svg]:active:fill-icon-active",
					"[&>*>svg]:focus:fill-icon-focus",
					"[&>input]:!ring-transparent",
					"[&>input]:!border-none",
					"[&>input]:!shadow-none",
					size === "sm" && "rounded h-8 text-sm",
					size === "md" && "rounded-md h-[38px] text-md",
					size === "lg" && "rounded-lg h-[46px] text-lg",
					fullWidth && "flex-1 w-full",
					noChrome && [
						"!border-none",
						"!shadow-none",
						"!ring-transparent",
						"!bg-transparent",
						"[&>input]:!p-0",
					],
				])}
				{...groupProps}
			>
				{incrementButtonPosition === "outside" && (
					<InputNumberButton
						size={size}
						variant="decrement"
						position="outside"
						{...decrementButtonProps}
					/>
				)}
				<input
					className={cx(
						"my-px",
						"px-2",
						"focus:my-0",
						"focus:shadow-border-brand-default",
						"focus:ring-4",
						"focus:outline-none",
						"disabled:cursor-not-allowed",
						"ring-default",
						"disabled:text-primary-disabled",
						"disabled:bg-tertiary"
					)}
					{...inputProps}
					ref={ref ?? inputRef}
				/>
				{incrementButtonPosition === "outside" && (
					<InputNumberButton
						size={size}
						variant="increment"
						position="outside"
						{...incrementButtonProps}
					/>
				)}
				{incrementButtonPosition === "right" && (
					<div className="flex flex-col">
						<InputNumberButton
							size={size}
							variant="increment"
							position="right"
							{...incrementButtonProps}
						/>
						<InputNumberButton
							size={size}
							variant="decrement"
							position="right"
							{...decrementButtonProps}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export const InputNumber = forwardRef(BaseInputNumber);
